import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Card, Tabs, Tab, Container } from 'react-bootstrap';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import FormContainer from '../../components/FormContainer';
import { toast } from 'react-toastify';

import {
  CATEGORY_OF_STAFF,
  REASON_FOR_REQUEST,
  SHIFT_STATUS,
  LINE_STATUS,
} from '../../utils/enums';

import {
  useGetShiftByIdQuery,
  useUpdateShiftMutation,
} from '../../slices/shiftApiSlice';
import { useGetHospitalLocationsQuery } from '../../slices/hospitalLocationApiSlice';


// Shift time presets (same as create form)
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};



const ShiftEditScreen = () => {
  const { id: shiftId } = useParams();
  const navigate = useNavigate();

  // Basic shift information
  const [hospitalLocationId, setHospitalLocationId] = useState('');
  const [wardDepartmentArea, setWardDepartmentArea] = useState('');
  const [reasonForRequest, setReasonForRequest] = useState('');
  const [reasonForRequestDetails, setReasonForRequestDetails] = useState('');
  const [categoryOfStaff, setCategoryOfStaff] = useState('');
  const [escort, setEscort] = useState(false);
  const [special, setSpecial] = useState(false);
  const [isOvertime, setIsOvertime] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [eccNumber, setEccNumber] = useState('');
  const [specialRequirements, setSpecialRequirements] = useState('');
  const [comments, setComments] = useState('');

  // Date and time
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('16:00');
  const [selectedPreset, setSelectedPreset] = useState('AM');

  const [activeTab, setActiveTab] = useState('basic');

  const {
    data: shift,
    isLoading: loadingShift,
    error: errorShift,
  } = useGetShiftByIdQuery(shiftId);

  const {
    data: hospitalLocations,
    isLoading: loadingLocations,
    error: errorLocations,
  } = useGetHospitalLocationsQuery();

  const [updateShift, { isLoading: loadingUpdate }] = useUpdateShiftMutation();

  useEffect(() => {
  if (shift && hospitalLocations?.length > 0) {
    // Parse date and time from ISO string
    let parsedStartTime = '08:00';
    let parsedEndTime = '16:00';
    let parsedStartDate = '';
    let parsedEndDate = '';

    if (shift.startDateTime) {
      const start = new Date(shift.startDateTime);
      parsedStartDate = start.toISOString().split('T')[0];
      parsedStartTime = start.toTimeString().slice(0, 5);
      setStartDate(parsedStartDate);
      setStartTime(parsedStartTime);
    }

    if (shift.endDateTime) {
      const end = new Date(shift.endDateTime);
      parsedEndDate = end.toISOString().split('T')[0];
      parsedEndTime = end.toTimeString().slice(0, 5);
      setEndDate(parsedEndDate);
      setEndTime(parsedEndTime);
    }

    const selectedHospitalId =
      typeof shift.hospitalLocationId === 'string'
        ? shift.hospitalLocationId
        : shift.hospitalLocationId?._id;

    setHospitalLocationId(selectedHospitalId);

    const selectedLocation = hospitalLocations.find(
      (loc) => loc._id === selectedHospitalId
    );
    setWardDepartmentArea(selectedLocation?.wardDepartmentArea || '');

    // Set basic information
    setReasonForRequest(shift.reasonForRequest || '');
    setReasonForRequestDetails(shift.reasonForRequestDetails || '');
    setCategoryOfStaff(shift.categoryOfStaff || '');
    setEscort(shift.escort || false);
    setSpecial(shift.special || false);
    setIsOvertime(shift.isOvertime || false);
    setIsActive(shift.isActive !== undefined ? shift.isActive : true);
    setEccNumber(shift.eccNumber || '');
    setSpecialRequirements(shift.specialRequirements || '');
    setComments(shift.comments || '');

    // Determine which preset matches the shift times
    // FIX: Use the parsed times, not the state variables
    const matchPreset = Object.keys(SHIFT_PRESETS).find(key => {
      const preset = SHIFT_PRESETS[key];
      return preset.start === parsedStartTime && preset.end === parsedEndTime;
    });
    
    if (matchPreset) {
      setSelectedPreset(matchPreset);
    } else {
      // Check if it might be an overnight shift (e.g., 20:00-08:00)
      // In that case, try to match with overnight presets
      const overnightMatch = Object.keys(SHIFT_PRESETS).find(key => {
        const preset = SHIFT_PRESETS[key];
        // For overnight shifts, the end time might be earlier than start time
        // So we need to handle that special case
        if (parsedStartTime > parsedEndTime) {
          return preset.start === parsedStartTime && preset.end === parsedEndTime;
        }
        return false;
      });
      
      if (overnightMatch) {
        setSelectedPreset(overnightMatch);
      } else {
        setSelectedPreset(''); // No preset matches
      }
    }
  }
}, [shift, hospitalLocations]); // Remove startTime/endTime from dependencies

  // Apply preset times when selected
  useEffect(() => {
    if (activeTab === 'schedule') {
      const preset = SHIFT_PRESETS[selectedPreset];
      if (preset) {
        setStartTime(preset.start);
        setEndTime(preset.end);
        
        // Update escort checkbox based on preset
        if (selectedPreset === 'ESCORT') {
          setEscort(true);
        }
      }
    }
  }, [selectedPreset, activeTab]);

  {/*
  // Combine date and time into ISO strings
  const combineDateTime = (dateStr, timeStr) => {
    const date = new Date(`${dateStr}T${timeStr}`);
    
    // Handle overnight shifts (end time earlier than start time)
    if (endTime < startTime && startDate === endDate) {
      date.setDate(date.getDate() + 1);
    }
    
    return date.toISOString();
  };
*/}

  // Combine date and time into ISO strings, handels overnight
const combineDateTime = (dateStr, timeStr) => {
  const date = new Date(`${dateStr}T${timeStr}`);
  return date.toISOString();
};


 const submitHandler = async (e) => {
  e.preventDefault();

  // Validate dates
  if (!startDate) {
    toast.error('Please select a start date');
    return;
  }
  
  if (!endDate) {
    toast.error('Please select an end date');
    return;
  }
  
  const startDateTimeObj = new Date(`${startDate}T${startTime}`);
  let endDateTimeObj = new Date(`${endDate}T${endTime}`);
  
  // Handle overnight shifts properly
  // Check if this is an overnight shift (end time earlier than start time on same day)
  const isOvernightShift = endTime < startTime && startDate === endDate;
  
  if (isOvernightShift) {
    // For overnight shifts, add a day to the end date for validation
    endDateTimeObj.setDate(endDateTimeObj.getDate() + 1);
  }
  
  if (endDateTimeObj <= startDateTimeObj) {
    toast.error('End date/time must be after start date/time');
    return;
  }

  try {
    // For the actual update, we need to handle overnight shifts differently
    let finalEndDate = endDate;
    let finalEndTime = endTime;
    
    if (isOvernightShift) {
      // For overnight shifts, we keep the same end date in the form
      // but the backend expects the next day's date
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);
      finalEndDate = nextDay.toISOString().split('T')[0];
    }
    
    await updateShift({
      shiftId,
      startDateTime: combineDateTime(startDate, startTime),
      endDateTime: combineDateTime(finalEndDate, finalEndTime),
      hospitalLocationId,
      wardDepartmentArea,
      reasonForRequest,
      reasonForRequestDetails,
      categoryOfStaff,
      escort,
      special,
      isOvertime,
      isActive,
      eccNumber,
      eccNumber,
      specialRequirements,
      comments,
    }).unwrap();
    toast.success('Shift updated successfully');
    navigate('/admin/shifts');
  } catch (err) {
    toast.error(err?.data?.message || err.error || 'Failed to update shift');
  }
};

  return (
    <>
      <Container fluid="lg">
        {loadingUpdate && <Loader />}
        {loadingShift || loadingLocations ? (
          <Loader />
        ) : errorShift ? (
          <Message variant="danger">
            {errorShift?.data?.message || errorShift.error}
          </Message>
        ) : errorLocations ? (
          <Message variant="danger">
            {errorLocations?.data?.message || errorLocations.error}
          </Message>
        ) : (
          <Form onSubmit={submitHandler}>
            <Card className="mb-4 custom-card-bg">
              <Card.Header className="fw-bold fs-4">
                Edit Shift
              </Card.Header>
              <Card.Body>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-4"
                >
                  <Tab eventKey="basic" title="Basic Information">
                    {/* Top Row: Hospital Location, Category, Reason */}
                    <Row className="mb-4">
                      <Col lg={4}>
                        <Form.Group controlId="hospitalLocationId">
                          <Form.Label className="fw-bold">Hospital Location</Form.Label>
                          <Form.Select
                            value={hospitalLocationId}
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              setHospitalLocationId(selectedId);
                              const selectedLocation = hospitalLocations.find(
                                (loc) => loc._id === selectedId
                              );
                              setWardDepartmentArea(selectedLocation?.wardDepartmentArea || '');
                            }}
                            required
                            size="lg"
                          >
                            <option value="">Select Hospital Location...</option>
                            {hospitalLocations.map((location) => (
                              <option key={location._id} value={location._id}>
                                {location.wardDepartmentArea} 
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col lg={4}>
                        <Form.Group controlId="categoryOfStaff">
                          <Form.Label className="fw-bold">Category of Staff</Form.Label>
                          <Form.Select
                            value={categoryOfStaff}
                            onChange={(e) => setCategoryOfStaff(e.target.value)}
                            required
                            size="lg"
                          >
                            <option value="">Select Category...</option>
                            {CATEGORY_OF_STAFF.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col lg={4}>
                        <Form.Group controlId="reasonForRequest">
                          <Form.Label className="fw-bold">Reason for Request</Form.Label>
                          <Form.Select
                            value={reasonForRequest}
                            onChange={(e) => setReasonForRequest(e.target.value)}
                            required
                            size="lg"
                          >
                            <option value="">Select Reason...</option>
                            {REASON_FOR_REQUEST.map((r) => (
                              <option key={r.value} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Middle Row: Reason Details, Options */}
                    <Row className="mb-4">
                      {/* Reason for Request Details */}
                      <Col lg={6}>
                        <Form.Group controlId="reasonForRequestDetails">
                          <Form.Label className="fw-bold">Reason for Request Details</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={reasonForRequestDetails}
                            onChange={(e) => setReasonForRequestDetails(e.target.value)}
                            placeholder="Provide details about why this shift is needed..."
                          />
                        </Form.Group>
                      </Col>

                      {/* Checkboxes and ECC Number */}
                      <Col lg={6}>
                        <Row>
                          {/* Checkboxes - Left side */}
                          <Col lg={6}>
                            <Form.Label className="fw-bold d-block">Options</Form.Label>
                            <div className="border rounded p-3 bg-light">
                              <Form.Group controlId="escort" className="mb-3">
                                <Form.Check
                                  type="checkbox"
                                  label={<span className="fw-bold">Escort Required</span>}
                                  checked={escort}
                                  onChange={(e) => setEscort(e.target.checked)}
                                />
                              </Form.Group>
                              <Form.Group controlId="special" className="mb-3">
                                <Form.Check
                                  type="checkbox"
                                  label={<span className="fw-bold">Is this a Special</span>}
                                  checked={special}
                                  onChange={(e) => setSpecial(e.target.checked)}
                                />
                              </Form.Group>
                              <Form.Group controlId="isOvertime" className="mb-3">
                                <Form.Check
                                  type="checkbox"
                                  label={<span className="fw-bold">Overtime Shift</span>}
                                  checked={isOvertime}
                                  onChange={(e) => setIsOvertime(e.target.checked)}
                                />
                              </Form.Group>
                              <Form.Group controlId="isActive">
                                <Form.Check
                                  type="checkbox"
                                  label={<span className="fw-bold">Active Shift</span>}
                                  checked={isActive}
                                  onChange={(e) => setIsActive(e.target.checked)}
                                />
                                <Form.Text className="text-muted">
                                  Uncheck to "delete" the shift (sets isActive=false)
                                </Form.Text>
                              </Form.Group>
                            </div>
                          </Col>

                          {/* ECC Number - Right side */}
                          <Col lg={6}>
                            <Form.Group controlId="eccNumber">
                              <Form.Label className="fw-bold">ECC Approval Number</Form.Label>
                              <Form.Control
                                type="text"
                                value={eccNumber}
                                onChange={(e) => setEccNumber(e.target.value)}
                                placeholder="Enter ECC number..."
                                size="lg"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    {/* Bottom Row: Special Requirements and Comments */}
                    <Row className="mb-4">
                      <Col lg={6}>
                        <Form.Group controlId="specialRequirements">
                          <Form.Label className="fw-bold">Special Requirements</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={specialRequirements}
                            onChange={(e) => setSpecialRequirements(e.target.value)}
                            placeholder="Any special equipment, training, or requirements..."
                          />
                        </Form.Group>
                      </Col>

                      <Col lg={6}>
                        <Form.Group controlId="comments">
                          <Form.Label className="fw-bold">Comments</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Additional notes or comments..."
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab>

                  <Tab eventKey="schedule" title="Schedule">
                    <Row className="mb-4">
                      <Col lg={12}>
                        <Form.Group controlId="shiftPreset">
                          <Form.Label className="fw-bold">Shift Time Preset</Form.Label>
                          <Row className="g-2">
                            {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                              <Col lg={3} md={4} sm={6} key={key}>
                                <div className={`border rounded p-2 cursor-pointer ${selectedPreset === key ? 'bg-primary text-white' : 'bg-light'}`}
                                     onClick={() => setSelectedPreset(key)}
                                     style={{cursor: 'pointer'}}>
                                  <Form.Check
                                    type="radio"
                                    id={`preset-${key}`}
                                    label={preset.label}
                                    name="shiftPreset"
                                    checked={selectedPreset === key}
                                    onChange={() => {}}
                                    className="mb-0"
                                  />
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col lg={3}>
                        <Form.Group controlId="startDate">
                          <Form.Label className="fw-bold">Start Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            required
                            size="lg"
                          />
                        </Form.Group>
                      </Col>

                      <Col lg={3}>
                        <Form.Group controlId="endDate">
                          <Form.Label className="fw-bold">End Date (Same Day)</Form.Label>
                          <Form.Control
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            required
                            size="lg"
                          />
                          <Form.Text className="text-muted">
                            Same day unless overnight shift
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col lg={3}>
                        <Form.Group controlId="startTime">
                          <Form.Label className="fw-bold">Start Time</Form.Label>
                          <Form.Control
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            required
                            size="lg"
                          />
                        </Form.Group>
                      </Col>

                      <Col lg={3}>
                        <Form.Group controlId="endTime">
                          <Form.Label className="fw-bold">End Time</Form.Label>
                          <Form.Control
                            type="time"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                            required
                            size="lg"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col lg={12}>
                        <div className="alert alert-info">
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <i className="bi bi-clock-history fs-4"></i>
                            </div>
                            <div>
                              <strong className="fs-5">Shift Duration:</strong>
                              <div className="mt-1">
                                {(() => {
                                  const start = new Date(`${startDate}T${startTime}`);
                                  let end = new Date(`${endDate}T${endTime}`);
                                  
                                  // Handle overnight shifts
                                  if (endTime < startTime && startDate === endDate) {
                                    end.setDate(end.getDate() + 1);
                                  }
                                  
                                  const durationMs = end - start;
                                  const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
                                  const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                                  
                                  return `${durationHours} hours ${durationMinutes} minutes`;
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Tab>
                </Tabs>

                <div className="d-flex justify-content-between pt-3 border-top">
                  <Button 
                    variant="secondary" 
                    onClick={() => navigate('/admin/shifts')}
                    size="lg"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" size="lg" disabled={loadingUpdate}>
                    {loadingUpdate ? 'Updating...' : 'Update Shift'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Form>
        )}
      </Container>
    </>
  );
};

export default ShiftEditScreen;


